import React from 'react';

import {Filter} from 'presentation/components/Filter';
import ModalDatePicker from 'presentation/components/ModalDatePicker';
import Icon from 'presentation/icons';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {parseDate, stringfyDate} from 'presentation/utils';
import {ConsultProtocolsTaggingEnum} from 'main/analytics/gaTagsEnum';

import {Drawer} from '../../../../../components/Drawer';
import {ORDERS} from '../../constants/requestParams';
import {useFilterByDatePicker, useFilters} from '../../hooks';
import {FilterHeader, StyledButton} from './styles';
import {HeaderFilterProps} from './types';
import {normalizeText} from 'presentation/utils/normalizeText';

const {clear, apply, filter, all} = translations['pt-br'].commons;
const {filters} = translations['pt-br'].privateFlow.consultProtocols;

export function HeaderFilter({
	openModalDatePicker,
	otherData,
	openModalExternally,
	openDrawerExternallyState,
	openDrawerExternally,
}: HeaderFilterProps) {
	const {isMobile} = useMediaQueries();

	const {SEARCH_PARAMS} = useQueryParams();
	const {initialDate, finalDate} = SEARCH_PARAMS;

	const {
		handleConfirmFilters,
		handleClearFilters,
		toggleDrawer,
		openDrawerState,
		setFilterOrderByState,
		setFilterProtocolTypeState,
		setFilterStatusState,
		filterOrderByState,
		filterProtocolTypeState,
		filterStatusState,
		getSelectedFiltersCount,
	} = useFilters();

	const {handleSelectFirstDate, handleSelectSecondDate, handleClearDates, getThreeMonthsAgo} =
		useFilterByDatePicker();

	const handleGetAllCategoryList = () => [
		{
			categoryTitle: filters.ordination,
			categoryItemsList: [
				{
					active: !filterOrderByState,
					title: filters.latest,
					dataTagId: ConsultProtocolsTaggingEnum.CLICK_FILTRO_ORDENACAO_MAIS_RECENTE,
					onClick: () => setFilterOrderByState(''),
				},
				{
					active: filterOrderByState === ORDERS.ASC,
					title: filters.earliest,
					dataTagId: ConsultProtocolsTaggingEnum.CLICK_FILTRO_ORDENACAO_MAIS_ANTIGO,
					onClick: () => setFilterOrderByState(ORDERS.ASC),
				},
			],
		},
		{
			categoryTitle: filters.protocolType,
			categoryItemsList: [
				{
					active: !filterProtocolTypeState,
					title: all,
					dataTagId: ConsultProtocolsTaggingEnum.CLICK_FILTRO_TIPO_TODOS,
					onClick: () => setFilterProtocolTypeState(''),
				},
				...(otherData?.protocolsTypes || []).map(protocol => ({
					active: filterProtocolTypeState === protocol,
					title: protocol,
					dataTagId: `${ConsultProtocolsTaggingEnum.CLICK_FILTRO_TIPO}${normalizeText(protocol)}`,
					onClick: () => setFilterProtocolTypeState(protocol),
				})),
			],
		},
		...((otherData?.statusTypes || []).length > 0 &&
		(otherData?.statusTypes || []).every(
			statusItem => statusItem?.statusDescricao && statusItem.statusDescricao.trim() !== '',
		)
			? [
					{
						categoryTitle: filters.status,
						categoryItemsList: [
							{
								active: !filterStatusState,
								title: all,
								dataTagId: ConsultProtocolsTaggingEnum.CLICK_FILTRO_STATUS_TODOS,
								onClick: () => setFilterStatusState(''),
							},
							...(otherData?.statusTypes || []).map(statusItem => ({
								active:
									filterStatusState.toString() === statusItem.statusId.toString(),
								title: statusItem.statusDescricao,
								dataTagId: `${ConsultProtocolsTaggingEnum.CLICK_FILTRO_STATUS}${normalizeText(
									statusItem.statusDescricao,
								)}`,
								onClick: () => setFilterStatusState(statusItem.statusId.toString()),
							})),
						],
					},
				]
			: []),
	];

	const drawerState = openDrawerExternallyState || openDrawerState;
	const handleDrawerAction = (shouldOpen: boolean) => {
		if (getSelectedFiltersCount() >= 2) {
			openDrawerExternally();
			return;
		}
		toggleDrawer(shouldOpen);
	};

	return (
		<FilterHeader id="header">
			<StyledButton
				variant="text"
				onClick={openModalExternally}
				data-tag-id={
					ConsultProtocolsTaggingEnum.CLICK_ALTERAR_INTERVALO_DATA_CONSULTA_PROTOCOLOS
				}
				endIcon={<Icon name="edit" size={20} />}>
				{initialDate || stringfyDate(getThreeMonthsAgo())} a{' '}
				{finalDate || stringfyDate(new Date())}
			</StyledButton>
			<ModalDatePicker
				externalOpen={openModalDatePicker}
				onAbandonModal={openModalExternally}
				currentFirstDate={parseDate(initialDate) || undefined}
				currentSecondDate={parseDate(finalDate) || undefined}
				selectFirstDate={handleSelectFirstDate}
				selectSecondDate={handleSelectSecondDate}
				showClearButton={true}
				onClearDates={handleClearDates}
				hideOpenButton={true}
				isSecondDateOptional
				adjustDateInterval={true}
				showNextMonthDay={false}
				showPrevMonthDay={false}
				shouldHaveDateIntervals={true}
			/>
			<Filter.Button
				data-tag-id={ConsultProtocolsTaggingEnum.CLICK_FILTRAR_CONSULTA_PROTOCOLOS}
				onClick={() => handleDrawerAction(true)}
				count={getSelectedFiltersCount()}
				sx={{margin: isMobile ? '12px 16px' : '12px 32px'}}>
				{filter}
			</Filter.Button>
			<Drawer.Root
				open={drawerState}
				onClose={() => handleDrawerAction(false)}
				onOpen={() => handleDrawerAction(true)}>
				<Drawer.Header
					title={filter}
					onClose={() => handleDrawerAction(false)}
					closeBtnDataTagId={
						ConsultProtocolsTaggingEnum.CLICK_BOTAO_FECHAR_FILTRO_CONSULTA_PROTOCOLOS
					}
				/>
				<Drawer.ContentListByCategory categoryList={handleGetAllCategoryList()} />
				<Drawer.Footer
					firstButtonDataTagId={
						ConsultProtocolsTaggingEnum.CLICK_LIMPAR_FILTRO_CONSULTA_PROTOCOLOS
					}
					firstButtonTitle={clear}
					firstButtonOnClick={() => handleClearFilters(() => handleDrawerAction(false))}
					secondButtonDataTagId={
						ConsultProtocolsTaggingEnum.CLICK_APLICAR_FILTRO_CONSULTA_PROTOCOLOS
					}
					secondButtonTitle={apply}
					secondButtonOnClick={() => {
						handleConfirmFilters(() => handleDrawerAction(false));
					}}
				/>
			</Drawer.Root>
		</FilterHeader>
	);
}
